import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Badge, Button, DashboardStatCard, HelpTooltip, Select } from "../../components/common/UI";
import { ScoreRing } from "../../components/common/ArcGauge";
import { scoreColor, scoreLabel } from "../../components/common/gapAssessmentTheme";
import { exportSubmissionPdf, safeExportFilename } from "../../utils/exportSubmissionPdf";
import { showError } from "../../utils/sweetAlert";
import ReportChartSection from "../../components/common/ReportCharts";
import {
  DEPT_ASSESSMENT_MODULES,
  DepartmentAssessmentsSection,
  assessmentStatusLabel,
  deptDisplayName,
  deptSerialNo,
  formatDeptProgress,
} from "../../components/common/DepartmentAssessmentReport";
import {
  buildReportPdfCharts,
  loadChartPrefs,
  moduleDetailPdfChart,
} from "../../utils/reportChartData";
import {
  filterReportDataSources,
  hasReportLicense,
  isReportFetchLicensed,
  tenantLicensedModules,
} from "../../utils/reportLicenses";
import {
  BarChart2,
  RefreshCw,
  FileDown,
  Building2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Cookie,
  FileText,
  Handshake,
  ClipboardCheck,
  Database,
  TrendingUp,
  LayoutList,
  Target,
  Unlock,
  Filter,
  Sparkles,
  GitCompare,
  BookOpen,
  Map,
  ShieldAlert,
} from "lucide-react";

const surfaceCard =
  "relative bg-white/60 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg ring-1 ring-slate-900/[0.04] overflow-hidden";

const premiumTopLine =
  "pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent";

const REPORT_TAB_META = {
  executive: {
    label: "Executive Report",
    description: "Compliance snapshot and key risk indicators for leadership review.",
    icon: Sparkles,
  },
  summary: {
    label: "Summary Report",
    description: "Module-by-module KPIs across your selected data scope.",
    icon: BarChart2,
  },
  detailed: {
    label: "Detailed Report",
    description: "Full metrics breakdown with timestamps and inventory details.",
    icon: LayoutList,
  },
};

const REPORT_METRIC_TIPS = {
  "Compliance Score": "Average score from the latest gap assessments in this data scope. A higher score means fewer gaps.",
  "Consent accepted": "Share of consent choices marked accepted. These counts are sent from the Consent Management module.",
  "Programme progress": "How much of the compliance roadmap is finished across Gap Assessment, ROPA, PIA, and TPRM.",
  "ROPA Progress": "How complete the record of processing activities is.",
  "Open Gaps": "Compliance gaps that are still open in the gap assessment.",
  "High-Risk Vendors": "Third parties whose assessment score is below 50%.",
  "Unencrypted PII": "Sensitive personal-data fields in privacy impact assessments that are not encrypted.",
  "Pending DSARs": "Requests from the DSAR module that still need a reply.",
  "Active Breaches": "Breach incidents that are still open.",
  "Consent Variance": "Records where stored consent does not match the current notice. Sent from the Consent module.",
  "Consent Events": "Consent choices sent from the Consent Management module.",
  "Acceptance Rate": "Share of choices marked accepted.",
  "Cookie Events": "Cookie banner choices sent from the Consent Management module.",
  "DSAR Requests": "Requests to see, correct, or delete personal data, sent from the DSAR module.",
  "PIA Assessments": "Privacy impact assessments recorded for this scope.",
  "Average Vendor Score": "Average risk score across third-party assessments.",
  "Sections Filled": "How many sections of the processing register have been completed.",
  "Data Records": "Sensitive records found by the DSPM scanner.",
  "Data Categories": "Types of personal data found, such as email addresses or phone numbers.",
  "Critical / High": "Open or recorded incidents marked critical or high severity.",
  "Reconsent Required": "People who need to be asked for consent again.",
  Policies: "Privacy policy documents stored for this organisation.",
  "AI Generated": "Policy documents created by the policy generator rather than uploaded.",
  "Roadmap Progress": "Average completion across Gap Assessment, ROPA, PIA, and TPRM.",
};

const DETAIL_ROW_TIPS = {
  "Total consent events": "Consent choices sent from the Consent Management module.",
  "Acceptance rate": "Share of recorded choices marked accepted.",
  Accepted: "Choices the person agreed to.",
  Revoked: "Choices that were accepted and later withdrawn.",
  Declined: "Choices the person refused.",
  "Total cookie events": "Cookie banner choices sent from the Consent Management module.",
  Rejected: "Cookie banners the person refused.",
  "Pending resolution": "Cookie choices that have not been accepted or rejected yet.",
  "Total requests": "Requests to see, correct, or delete personal data, sent from the DSAR module.",
  Processed: "DSAR requests that already have a reply.",
  Pending: "DSAR requests that are still waiting for a reply.",
  "Rejected / other": "DSAR requests that were refused or closed another way.",
  "Completion rate": "Share of requests that have been processed.",
  "Total assessments": "Privacy impact assessments recorded for this scope.",
  "Sensitive processing flagged": "Assessments that include sensitive personal data.",
  "Encrypted fields": "Sensitive fields that are protected by encryption.",
  "Unencrypted fields": "Sensitive fields that are stored without encryption.",
  "Encryption coverage": "Share of sensitive fields that are encrypted.",
  "Total vendor assessments": "Third-party risk assessments in this scope.",
  Submitted: "Vendor assessments that have been sent in.",
  "Pending / draft": "Vendor assessments that are started but not submitted.",
  "High risk (<50%)": "Vendors whose score is below 50%.",
  "Average risk score": "Average score across vendor assessments. A higher score is safer.",
  "Submission rate": "Share of vendor assessments that have been submitted.",
  "Sections completed": "Gap-assessment sections marked complete.",
  "Questions answered": "Gap-assessment questions that have an answer.",
  "Gaps identified": "Requirements that are not met yet.",
  Status: "Whether the gap assessment is submitted, still a draft, or not started.",
  "Sections filled": "ROPA sections that have been completed.",
  Completion: "How much of the processing register is filled.",
  "Submitted records": "ROPA records that have been submitted.",
  "Draft in progress": "A ROPA draft exists and has not been submitted.",
  "Workflow status": "Current stage of the processing register.",
  "Total data records": "Sensitive records found by the DSPM scanner.",
  "Total incidents": "Breach reports recorded for this organisation.",
  "Manual reports": "Incidents someone entered by hand.",
  "Agent reports": "Incidents raised by the monitoring agent.",
  "Active / open": "Incidents that are not resolved yet.",
  "Pending review": "Incidents waiting for someone to review them.",
  Resolved: "Incidents that have been closed.",
  Investigating: "Incidents that are still being looked into.",
  "Critical / high severity": "Open or recorded incidents marked critical or high.",
  "Data principals affected": "People whose personal data was involved.",
  "Records impacted": "Data records affected by the incidents.",
  "Resolution rate": "Share of incidents that have been resolved.",
  "Total records": "Consent records checked against the current notice.",
  Aligned: "Records whose stored consent matches the current notice.",
  "With variance": "Records whose stored consent does not match the current notice.",
  "Reconsent required": "People who need to be asked for consent again.",
  "Reconsent pending": "Reconsent requests that have not been completed.",
  "Reconsent completed": "People who have given consent again.",
  "Alignment rate": "Share of records that match the current notice.",
  "Total policies": "Privacy policy documents stored for this organisation.",
  "Uploaded documents": "Policies someone uploaded.",
  "AI-generated documents": "Policies created by the policy generator.",
  "AI provider configured": "Whether an AI service is set up to generate policies.",
  "Overall progress": "Average completion across Gap Assessment, ROPA, PIA, and TPRM.",
  "Departments tracked": "Departments included in the roadmap.",
  "Users tracked": "People included in the roadmap counts.",
  "Gap assessment": "Roadmap completion for gap assessments, and how many departments are finished.",
  ROPA: "Roadmap completion for the processing register, and how many departments are finished.",
  PIA: "Roadmap completion for privacy impact assessments, and how many departments are finished.",
  TPRM: "Roadmap completion for third-party risk, and how many departments are finished.",
};

const MODULE_META = {
  consent: {
    title: "Consent Management",
    tooltip: "Counts sent from the Consent Management module: accepted, later revoked, or declined.",
    icon: ShieldCheck,
    accent: "bg-emerald-50 text-emerald-600",
    border: "border-l-emerald-500",
    gradient: "from-emerald-400 to-teal-600",
  },
  cookie: {
    title: "Cookie Compliance",
    tooltip: "Cookie banner choices sent from the Consent Management module: accepted, rejected, or still pending.",
    icon: Cookie,
    accent: "bg-amber-50 text-amber-600",
    border: "border-l-amber-500",
    gradient: "from-amber-400 to-orange-500",
  },
  dsar: {
    title: "DSAR / Data Requests",
    tooltip: "Requests sent from the DSAR module to access, correct, or delete personal data, including how many are still open.",
    icon: FileText,
    accent: "bg-blue-50 text-blue-600",
    border: "border-l-blue-500",
    gradient: "from-sky-400 to-blue-600",
  },
  pia: {
    title: "Privacy Impact Assessment",
    tooltip: "Assessments of processing that could affect people's privacy, including sensitive and unencrypted fields.",
    icon: ClipboardCheck,
    accent: "bg-indigo-50 text-indigo-600",
    border: "border-l-indigo-500",
    gradient: "from-indigo-400 to-blue-600",
  },
  tprm: {
    title: "Third-Party Risk",
    tooltip: "Vendor assessments: how many are submitted, still in draft, or scored as high risk.",
    icon: Handshake,
    accent: "bg-teal-50 text-teal-600",
    border: "border-l-teal-500",
    gradient: "from-teal-400 to-cyan-600",
  },
  gap: {
    title: "Gap Assessment",
    tooltip: "DPDP readiness review: score, sections completed, and gaps that are still open.",
    icon: Target,
    accent: "bg-indigo-50 text-indigo-600",
    border: "border-l-indigo-500",
    gradient: "from-indigo-400 to-blue-600",
  },
  ropa: {
    title: "ROPA",
    tooltip: "Record of Processing Activities: how much of the register is filled and whether it has been submitted.",
    icon: LayoutList,
    accent: "bg-indigo-50 text-indigo-600",
    border: "border-l-indigo-500",
    gradient: "from-indigo-400 to-blue-600",
  },
  dspm: {
    title: "Data Security (DSPM)",
    tooltip: "Sensitive data found by the scanner, grouped by type such as email, phone, and identity numbers.",
    icon: Database,
    accent: "bg-emerald-50 text-emerald-600",
    border: "border-l-emerald-500",
    gradient: "from-emerald-400 to-green-600",
  },
  breach: {
    title: "Breach Management",
    tooltip: "Security incidents: how many are open, waiting for review, or already resolved.",
    icon: ShieldAlert,
    accent: "bg-red-50 text-red-600",
    border: "border-l-red-500",
    gradient: "from-red-500 to-rose-600",
  },
  consent_variance: {
    title: "Consent Variance",
    tooltip: "Records from the Consent module where stored consent does not match the current notice.",
    icon: GitCompare,
    accent: "bg-rose-50 text-rose-600",
    border: "border-l-rose-500",
    gradient: "from-rose-400 to-pink-600",
  },
  policy_store: {
    title: "Policy Store",
    tooltip: "Privacy policies saved for the organisation, whether uploaded or generated.",
    icon: BookOpen,
    accent: "bg-violet-50 text-violet-600",
    border: "border-l-violet-500",
    gradient: "from-violet-400 to-purple-600",
  },
  roadmap: {
    title: "Compliance Roadmap",
    tooltip: "Progress across Gap Assessment, ROPA, PIA, and TPRM for the departments you track.",
    icon: Map,
    accent: "bg-cyan-50 text-cyan-600",
    border: "border-l-cyan-500",
    gradient: "from-cyan-400 to-blue-600",
  },
};

const REPORT_TABS = [
  { id: "executive", label: "Executive Report" },
  { id: "summary", label: "Summary Report" },
  { id: "detailed", label: "Detailed Report" },
];

const DATA_SOURCES = [
  { id: "all", label: "All Privacy Data" },
  { id: "consent", label: "Consent Management" },
  { id: "cookie", label: "Cookie Compliance" },
  { id: "dsar", label: "DSAR / Data Requests" },
  { id: "pia", label: "Privacy Impact Assessment" },
  { id: "tprm", label: "Third-Party Risk (TPRM)" },
  { id: "gap", label: "Gap Assessment" },
  { id: "ropa", label: "ROPA" },
  { id: "dspm", label: "Data Security (DSPM)" },
  { id: "breach", label: "Breach Management" },
  { id: "consent_variance", label: "Consent Variance" },
  { id: "policy_store", label: "Policy Store" },
  { id: "roadmap", label: "Compliance Roadmap" },
];

function sumStatValues(...vals) {
  return vals.reduce((sum, v) => sum + (Number(v) || 0), 0);
}

function effectiveStatTotal(apiTotal, ...parts) {
  const sum = sumStatValues(...parts);
  const api = Number(apiTotal) || 0;
  return Math.max(api, sum);
}

function formatStatPct(value, total) {
  const t = Number(total) || 0;
  const v = Number(value) || 0;
  if (t <= 0) return "0%";
  return `${Math.min(100, Math.round((v / t) * 100))}%`;
}

function parseRoadmapModule(mod) {
  if (!mod) {
    return {
      deptComplete: 0,
      deptTotal: 0,
      usersComplete: 0,
      usersTotal: 0,
      progressPct: 0,
      allComplete: false,
    };
  }
  const deptComplete = mod.departments_complete ?? 0;
  const deptTotal = mod.departments_total ?? 0;
  return {
    deptComplete,
    deptTotal,
    usersComplete: mod.users_complete ?? 0,
    usersTotal: mod.users_total ?? 0,
    progressPct: deptTotal > 0 ? Math.min(100, Math.round((deptComplete / deptTotal) * 100)) : (mod.progress_pct ?? 0),
    allComplete: deptTotal > 0 && deptComplete >= deptTotal,
  };
}

async function fetchReportJson(path) {
  try {
    const res = await apiFetch(path);
    if (!res.ok) {
      return { ok: false, status: res.status, data: null };
    }
    return { ok: true, data: await res.json() };
  } catch (err) {
    return { ok: false, error: err?.message || "Request failed", data: null };
  }
}

function formatTimestamp(ts, { loading = false } = {}) {
  if (loading) return "Loading…";
  if (!ts) return "Not available";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDeptSubmittedAt(ts) {
  if (!ts) return "—";
  return formatTimestamp(ts);
}

function pushDepartmentAssessmentPdfSections(sections, data, include, { fullTable }) {
  for (const mod of DEPT_ASSESSMENT_MODULES) {
    if (!include(mod.id)) continue;
    const payload = data.departmentAssessments?.[mod.key];
    if (!payload) continue;
    const summary = payload.summary || {};
    const cycleName = payload.cycle?.name;
    const heading = `${mod.title} by Department`;
    const rows = [
      ...(cycleName ? [{ label: "Assessment cycle", value: cycleName }] : []),
      { label: "Submitted", value: String(summary.submitted ?? 0) },
      { label: "In progress", value: String(summary.in_progress ?? 0) },
      { label: "Pending", value: String(summary.pending ?? 0) },
    ];
    const table = fullTable
      ? {
          headers: ["S.No", "Department", "Status", "Score", "Submitted", "Lock", "Progress"],
          rows: (payload.departments || []).map((d, index) => [
            deptSerialNo(d, index),
            deptDisplayName(d),
            assessmentStatusLabel(d.status),
            d.score != null ? `${d.score}%` : "—",
            formatDeptSubmittedAt(d.submitted_at),
            d.is_locked ? "Locked" : "Unlocked",
            formatDeptProgress(d),
          ]),
        }
      : undefined;
    sections.push({
      heading,
      rows,
      ...(table ? { table } : {}),
    });
    const pendingNames =
      (payload.pending_departments || []).map((d) => d.department_name).filter(Boolean).join(", ") || "None";
    const inProgressNames =
      (payload.in_progress_departments || []).map((d) => d.department_name).filter(Boolean).join(", ") || "None";
    sections.push({
      heading: `${mod.title} — Departments still open`,
      rows: [
        { label: "Pending departments", value: pendingNames },
        { label: "In-progress departments", value: inProgressNames },
      ],
    });
  }
}

function executiveKpiIcon(label) {
  const icons = {
    "Open Gaps": Target,
    "High-Risk Vendors": AlertTriangle,
    "Unencrypted PII": Unlock,
    "Pending DSARs": Clock,
    "Active Breaches": ShieldAlert,
    "Consent Variance": GitCompare,
    "Consent Events": ShieldCheck,
    "Consent accepted": CheckCircle2,
    "Programme progress": Map,
    "Acceptance Rate": CheckCircle2,
    "Cookie Events": Cookie,
    "DSAR Requests": FileText,
    "PIA Assessments": ClipboardCheck,
    "Average Vendor Score": Handshake,
    "ROPA Progress": LayoutList,
    "Sections Filled": LayoutList,
    "Data Records": Database,
    "Data Categories": Database,
    "Critical / High": ShieldAlert,
    "Reconsent Required": GitCompare,
    Policies: BookOpen,
    "AI Generated": Sparkles,
    "Roadmap Progress": Map,
  };
  const Icon = icons[label] || BarChart2;
  return <Icon size={18} className="text-white" />;
}

function MetricRow({ label, value, highlight }) {
  const tip = DETAIL_ROW_TIPS[label];
  return (
    <div className="group flex items-center justify-between gap-3 py-2.5 px-1 -mx-1 rounded-lg border-b border-slate-100/90 last:border-0 hover:bg-slate-50/70 transition-colors duration-200">
      <span className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
        <span className="w-1 h-1 rounded-full bg-slate-300 group-hover:bg-blue-500 transition-colors flex-shrink-0" />
        <span className="inline-flex items-center gap-1 min-w-0">
          {label}
          {tip ? <HelpTooltip content={tip} size={12} position="bottom" /> : null}
        </span>
      </span>
      <span
        className={`text-sm font-bold tabular-nums tracking-tight ${highlight ? "text-red-600" : "text-slate-900"}`}
        style={{ fontFamily: "Sora, sans-serif" }}
      >
        {value}
      </span>
    </div>
  );
}

function PremiumIconBadge({ gradient, children, size = "md" }) {
  const dim = size === "lg" ? "w-12 h-12 rounded-2xl" : "w-9 h-9 rounded-xl";
  return (
    <div className={`relative ${dim} flex-shrink-0`}>
      <div
        className={`absolute inset-0 bg-gradient-to-br ${gradient} rounded-[inherit] opacity-30 blur-md scale-110`}
        aria-hidden
      />
      <div
        className={`relative ${dim} bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg shadow-blue-500/15 ring-1 ring-white/30`}
      >
        {children}
      </div>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <p className="text-[10px] text-slate-500 uppercase tracking-[0.14em] font-semibold">
      {children}
    </p>
  );
}

function DetailSection({ moduleId, subtitle, children, loading, extra }) {
  const meta = MODULE_META[moduleId];
  const Icon = meta?.icon || FileText;
  return (
    <div className={`${surfaceCard} border-l-[3px] ${meta?.border || "border-l-slate-400"}`}>
      <span className={premiumTopLine} />
      <div className="px-5 py-4 border-b border-slate-100/80 bg-gradient-to-r from-slate-50/70 via-white/50 to-blue-50/30 backdrop-blur-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <PremiumIconBadge gradient={meta?.gradient || "from-slate-400 to-slate-600"} size="lg">
              <Icon size={20} className="text-white drop-shadow-sm" />
            </PremiumIconBadge>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-slate-900 font-display inline-flex items-center gap-1">
                {meta?.title || moduleId} — Detailed
                {meta?.tooltip ? (
                  <HelpTooltip content={meta.tooltip} size={13} position="bottom" maxWidth={320} />
                ) : null}
              </h2>
              {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {extra}
        </div>
      </div>
      <div className="px-5 py-4">
        {loading ? (
          <div className="grid sm:grid-cols-2 gap-x-8 animate-pulse">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-9 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 rounded-lg mb-2" />
            ))}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function RiskSeverityBadge({ severity }) {
  const variant =
    severity === "High" ? "danger" : severity === "Medium" ? "warning" : "default";
  return <Badge variant={variant}>{severity}</Badge>;
}

const RISK_MODULE_BY_AREA = {
  "Gap Assessment": "gap",
  "Third-Party Risk": "tprm",
  PIA: "pia",
  DSAR: "dsar",
  ROPA: "ropa",
  "Breach Management": "breach",
  "Consent Variance": "consent_variance",
  "Policy Store": "policy_store",
  "Compliance Roadmap": "roadmap",
};

function filterRiskItems(items, include) {
  return (items || []).filter((item) => {
    const moduleId = RISK_MODULE_BY_AREA[item.area];
    return moduleId ? include(moduleId) : false;
  });
}

function buildSummaryModuleRows(data, include) {
  const rows = [];
  if (include("consent")) {
    rows.push(["Consent", `${data.consentAccepted ?? 0} accepted`, formatStatPct(data.consentAccepted, data.consentTotal)]);
  }
  if (include("cookie")) {
    rows.push(["Cookie", `${data.cookieAccepted ?? 0} accepted`, formatStatPct(data.cookieAccepted, data.cookieTotal)]);
  }
  if (include("dsar")) {
    rows.push(["DSAR", `${data.dsarProcessed ?? 0} processed`, formatStatPct(data.dsarProcessed, data.dsarTotal)]);
  }
  if (include("pia")) {
    rows.push(["PIA", `${data.piaTotal ?? 0} assessments`, `${data.piaSensitive ?? 0} sensitive`]);
  }
  if (include("tprm")) {
    rows.push(["TPRM", `${data.tprmSubmitted ?? 0} submitted`, data.tprmAvgScore != null ? `Avg ${data.tprmAvgScore}%` : "—"]);
  }
  if (include("gap")) {
    rows.push(["Gap Assessment", data.gapComplianceLabel || "—", data.gapScore != null ? `${data.gapScore}%` : "—"]);
  }
  if (include("ropa")) {
    rows.push(["ROPA", `${data.ropaSectionsFilled ?? 0}/${data.ropaSectionsTotal ?? 0} sections`, `${data.ropaProgressPct ?? 0}%`]);
  }
  if (include("dspm")) {
    rows.push(["DSPM", `${data.dspmTotal ?? 0} records`, `${data.dspmBreakdown?.length ?? 0} categories`]);
  }
  if (include("breach")) {
    rows.push(["Breach Management", `${data.breachActive ?? 0} active`, `${data.breachResolved ?? 0} resolved`]);
  }
  if (include("consent_variance")) {
    rows.push(["Consent Variance", `${data.varianceWithVariance ?? 0} with variance`, `${data.varianceAligned ?? 0} aligned`]);
  }
  if (include("policy_store")) {
    rows.push(["Policy Store", `${data.policyTotal ?? 0} policies`, data.policyAiConfigured ? "AI enabled" : "Manual only"]);
  }
  if (include("roadmap")) {
    rows.push([
      "Compliance Roadmap",
      `${data.roadmapOverallPct ?? 0}% overall`,
      `${data.roadmapModulesComplete ?? 0}/${data.roadmapModulesTracked ?? 0} modules complete`,
    ]);
  }
  return rows;
}

function buildExecutiveMetrics(data, include, source) {
  const metrics = [];
  const push = (metric) => {
    if (metric) metrics.push(metric);
  };

  if (source === "all") {
    push(include("gap") && {
      label: "Compliance Score",
      value: data.complianceScore != null ? `${data.complianceScore}%` : "—",
      color: data.complianceScore != null ? scoreColor(data.complianceScore) : undefined,
      sub: data.complianceLabel || "Organisation score",
    });
    push(include("consent") && {
      label: "Consent accepted",
      value: formatStatPct(data.consentAccepted, data.consentTotal),
      tone: (data.consentTotal ?? 0) > 0 && (data.consentAccepted ?? 0) / data.consentTotal < 0.5 ? "amber" : "emerald",
      sub: `${data.consentAccepted ?? 0} of ${data.consentTotal ?? 0} consent choices`,
    });
    push(include("roadmap") && {
      label: "Programme progress",
      value: `${data.roadmapOverallPct ?? 0}%`,
      sub: `${data.roadmapModulesComplete ?? 0} of ${data.roadmapModulesTracked ?? 0} modules finished`,
    });
    push(!include("roadmap") && include("ropa") && {
      label: "ROPA Progress",
      value: `${data.ropaProgressPct ?? 0}%`,
      sub: "Processing register completed",
    });
    push(include("gap") && {
      label: "Open Gaps",
      value: String(data.gapGaps ?? 0),
      tone: (data.gapGaps ?? 0) > 0 ? "red" : "emerald",
      sub: (data.gapGaps ?? 0) > 0 ? "Needs attention" : "All clear",
    });
    push(include("tprm") && {
      label: "High-Risk Vendors",
      value: String(data.tprmHighRisk ?? 0),
      tone: (data.tprmHighRisk ?? 0) > 0 ? "red" : "emerald",
      sub: "Below 50% score",
    });
    push(include("pia") && {
      label: "Unencrypted PII",
      value: String(data.piaUnencrypted ?? 0),
      tone: (data.piaUnencrypted ?? 0) > 0 ? "amber" : "emerald",
      sub: "Sensitive fields",
    });
    push(include("dsar") && {
      label: "Pending DSARs",
      value: String(data.dsarPending ?? 0),
      tone: (data.dsarPending ?? 0) > 0 ? "amber" : "emerald",
      sub: "Awaiting action",
    });
    push(include("breach") && {
      label: "Active Breaches",
      value: String(data.breachActive ?? 0),
      tone: (data.breachActive ?? 0) > 0 ? "red" : "emerald",
      sub: (data.breachCriticalHigh ?? 0) > 0 ? `${data.breachCriticalHigh} critical/high` : "Incidents open",
    });
    push(include("consent_variance") && {
      label: "Consent Variance",
      value: String(data.varianceWithVariance ?? 0),
      tone: (data.varianceReconsentRequired ?? 0) > 0 ? "amber" : "emerald",
      sub: (data.varianceReconsentRequired ?? 0) > 0
        ? `${data.varianceReconsentRequired} need reconsent`
        : "Records aligned",
    });
    return metrics;
  }

  if (source === "gap" && include("gap")) {
    push({
      label: "Compliance Score",
      value: data.complianceScore != null ? `${data.complianceScore}%` : "—",
      color: data.complianceScore != null ? scoreColor(data.complianceScore) : undefined,
      sub: data.complianceLabel || data.gapComplianceLabel || "Gap assessment",
    });
    push({
      label: "Open Gaps",
      value: String(data.gapGaps ?? 0),
      tone: (data.gapGaps ?? 0) > 0 ? "red" : "emerald",
      sub: (data.gapGaps ?? 0) > 0 ? "Needs attention" : "All clear",
    });
  }
  if (source === "consent" && include("consent")) {
    push({ label: "Consent Events", value: String(data.consentTotal ?? 0), sub: "Recorded choices" });
    push({
      label: "Acceptance Rate",
      value: formatStatPct(data.consentAccepted, data.consentTotal),
      sub: `${data.consentAccepted ?? 0} accepted`,
    });
  }
  if (source === "cookie" && include("cookie")) {
    push({ label: "Cookie Events", value: String(data.cookieTotal ?? 0), sub: "Banner choices" });
    push({
      label: "Acceptance Rate",
      value: formatStatPct(data.cookieAccepted, data.cookieTotal),
      sub: `${data.cookiePending ?? 0} still pending`,
    });
  }
  if (source === "dsar" && include("dsar")) {
    push({ label: "DSAR Requests", value: String(data.dsarTotal ?? 0), sub: "Access requests" });
    push({
      label: "Pending DSARs",
      value: String(data.dsarPending ?? 0),
      tone: (data.dsarPending ?? 0) > 0 ? "amber" : "emerald",
      sub: "Awaiting action",
    });
  }
  if (source === "pia" && include("pia")) {
    push({ label: "PIA Assessments", value: String(data.piaTotal ?? 0), sub: "Impact assessments" });
    push({
      label: "Unencrypted PII",
      value: String(data.piaUnencrypted ?? 0),
      tone: (data.piaUnencrypted ?? 0) > 0 ? "amber" : "emerald",
      sub: "Sensitive fields",
    });
  }
  if (source === "tprm" && include("tprm")) {
    push({
      label: "Average Vendor Score",
      value: data.tprmAvgScore != null ? `${data.tprmAvgScore}%` : "—",
      sub: `${data.tprmTotal ?? 0} assessments`,
    });
    push({
      label: "High-Risk Vendors",
      value: String(data.tprmHighRisk ?? 0),
      tone: (data.tprmHighRisk ?? 0) > 0 ? "red" : "emerald",
      sub: "Below 50% score",
    });
  }
  if (source === "ropa" && include("ropa")) {
    push({ label: "ROPA Progress", value: `${data.ropaProgressPct ?? 0}%`, sub: data.ropaStatus || "Register" });
    push({
      label: "Sections Filled",
      value: `${data.ropaSectionsFilled ?? 0}/${data.ropaSectionsTotal ?? 0}`,
      sub: `${data.ropaSubmitted ?? 0} submitted`,
    });
  }
  if (source === "dspm" && include("dspm")) {
    push({ label: "Data Records", value: String(data.dspmTotal ?? 0), sub: "Discovered inventory" });
    push({ label: "Data Categories", value: String(data.dspmBreakdown?.length ?? 0), sub: "Types found" });
  }
  if (source === "breach" && include("breach")) {
    push({
      label: "Active Breaches",
      value: String(data.breachActive ?? 0),
      tone: (data.breachActive ?? 0) > 0 ? "red" : "emerald",
      sub: `${data.breachTotal ?? 0} total incidents`,
    });
    push({
      label: "Critical / High",
      value: String(data.breachCriticalHigh ?? 0),
      tone: (data.breachCriticalHigh ?? 0) > 0 ? "red" : "emerald",
      sub: `${data.breachResolved ?? 0} resolved`,
    });
  }
  if (source === "consent_variance" && include("consent_variance")) {
    push({
      label: "Consent Variance",
      value: String(data.varianceWithVariance ?? 0),
      tone: (data.varianceWithVariance ?? 0) > 0 ? "amber" : "emerald",
      sub: `${data.varianceTotal ?? 0} records`,
    });
    push({
      label: "Reconsent Required",
      value: String(data.varianceReconsentRequired ?? 0),
      tone: (data.varianceReconsentRequired ?? 0) > 0 ? "amber" : "emerald",
      sub: `${data.varianceReconsentPending ?? 0} pending`,
    });
  }
  if (source === "policy_store" && include("policy_store")) {
    push({ label: "Policies", value: String(data.policyTotal ?? 0), sub: data.policyAiConfigured ? "AI enabled" : "Manual only" });
    push({ label: "AI Generated", value: String(data.policyGenerated ?? 0), sub: `${data.policyUploaded ?? 0} uploaded` });
  }
  if (source === "roadmap" && include("roadmap")) {
    push({
      label: "Roadmap Progress",
      value: `${data.roadmapOverallPct ?? 0}%`,
      sub: `${data.roadmapModulesComplete ?? 0}/${data.roadmapModulesTracked ?? 0} modules complete`,
    });
  }
  return metrics;
}

function buildReportSections({ tab, source, data, orgName, chartPrefs, showModuleFn }) {
  const sections = [];
  const sourceLabel = DATA_SOURCES.find((s) => s.id === source)?.label || "All Privacy Data";
  const generatedAt = new Date().toLocaleString("en-IN");
  const showModule = showModuleFn || ((id) => source === "all" || source === id);
  const include = showModule;
  const licensedFor = (id) => hasReportLicense(data.licensedModules, id);
  const scopedRisks = filterRiskItems(data.riskItems, include);
  const chartData = { ...data, riskItems: scopedRisks };
  const pdfCharts = buildReportPdfCharts({
    data: chartData,
    source,
    showModule,
    chartPrefs: chartPrefs || loadChartPrefs(),
    tab,
  });

  sections.push({
    heading: "Report Information",
    rows: [
      { label: "Organization", value: orgName || "—" },
      { label: "Report Type", value: REPORT_TABS.find((t) => t.id === tab)?.label || tab },
      { label: "Data Scope", value: sourceLabel },
      { label: "Level of Detail", value: REPORT_TAB_META[tab]?.description || "—" },
      { label: "Generated At", value: generatedAt },
    ],
  });

  if (tab === "executive") {
    const metrics = buildExecutiveMetrics(data, include, source).map(({ label, value, color }) => ({
      label,
      value,
      ...(color ? { color } : {}),
    }));
    sections.push({
      heading: "Executive Overview",
      rows: [
        {
          label: "What this report includes",
          value: "Leadership figures and open risks only. Module breakdowns are in the Summary and Detailed reports.",
        },
      ],
      ...(metrics.length ? { metrics } : {}),
      ...(pdfCharts.length ? { charts: pdfCharts } : {}),
    });

    sections.push({
      heading: "Key Risk Indicators",
      rows: scopedRisks.length
        ? undefined
        : [{ label: "Result", value: "No open risks in the selected data scope." }],
      ...(scopedRisks.length
        ? {
            table: {
              headers: ["Area", "Finding", "Severity"],
              rows: scopedRisks.map((r) => [r.area, r.finding, r.severity]),
            },
          }
        : {}),
    });
    pushDepartmentAssessmentPdfSections(sections, data, showModule, { fullTable: false });
  }

  if (tab === "summary") {
    const rows = buildSummaryModuleRows(data, include);
    sections.push({
      heading: "Module Summary",
      rows: [
        {
          label: "What this report includes",
          value: "One headline figure per module in the selected data scope. Timestamps and inventories are in the Detailed report.",
        },
      ],
      ...(pdfCharts.length ? { charts: pdfCharts } : {}),
      ...(rows.length
        ? { table: { headers: ["Module", "Primary Metric", "Secondary"], rows } }
        : {}),
    });
    pushDepartmentAssessmentPdfSections(sections, data, showModule, { fullTable: false });
  }

  if (tab === "detailed") {
    if (pdfCharts.length) {
      sections.push({
        heading: "Detailed Breakdown",
        rows: [
          {
            label: "What this report includes",
            value: "Full metrics, timestamps, and inventories for each module in the selected data scope.",
          },
          {
            label: "Chart configuration",
            value: `Primary: ${chartPrefs?.primaryType || "bar"}, Secondary: ${chartPrefs?.secondaryType || "pie"}`,
          },
        ],
        charts: pdfCharts,
      });
    }

    const pushDetail = (moduleId, heading, rows, extra = {}) => {
      const detailChart = moduleDetailPdfChart(moduleId, data);
      sections.push({
        heading,
        rows,
        ...(detailChart ? { charts: [detailChart] } : {}),
        ...extra,
      });
    };

    if (include("consent")) {
      pushDetail("consent", "Consent Management", [
          { label: "Total Events", value: String(data.consentTotal ?? 0) },
          { label: "Accepted", value: String(data.consentAccepted ?? 0) },
          { label: "Revoked", value: String(data.consentRevoked ?? 0) },
          { label: "Declined", value: String(data.consentDeclined ?? 0) },
          { label: "Last Updated", value: formatTimestamp(data.consentUpdatedAt) },
      ]);
    }
    if (include("cookie")) {
      pushDetail("cookie", "Cookie Compliance", [
          { label: "Total Events", value: String(data.cookieTotal ?? 0) },
          { label: "Accepted", value: String(data.cookieAccepted ?? 0) },
          { label: "Rejected", value: String(data.cookieRejected ?? 0) },
          { label: "Pending", value: String(data.cookiePending ?? 0) },
          { label: "Last Updated", value: formatTimestamp(data.cookieUpdatedAt) },
      ]);
    }
    if (include("dsar")) {
      pushDetail("dsar", "DSAR / Data Subject Requests", [
          { label: "Total Requests", value: String(data.dsarTotal ?? 0) },
          { label: "Processed", value: String(data.dsarProcessed ?? 0) },
          { label: "Pending", value: String(data.dsarPending ?? 0) },
          { label: "Rejected / Other", value: String(data.dsarRejected ?? 0) },
          { label: "Last Updated", value: formatTimestamp(data.dsarUpdatedAt) },
      ]);
    }
    if (include("pia")) {
      pushDetail("pia", "Privacy Impact Assessment", [
          { label: "Total Assessments", value: String(data.piaTotal ?? 0) },
          { label: "Sensitive Processing", value: String(data.piaSensitive ?? 0) },
          { label: "Encrypted Fields", value: String(data.piaEncrypted ?? 0) },
          { label: "Unencrypted Fields", value: String(data.piaUnencrypted ?? 0) },
          { label: "Last Activity", value: formatTimestamp(data.piaLastUsedAt) },
      ]);
    }
    if (include("tprm")) {
      pushDetail("tprm", "Third-Party Risk (TPRM)", [
          { label: "Total Assessments", value: String(data.tprmTotal ?? 0) },
          { label: "Submitted", value: String(data.tprmSubmitted ?? 0) },
          { label: "Pending / Draft", value: String(data.tprmPending ?? 0) },
          { label: "High Risk (<50%)", value: String(data.tprmHighRisk ?? 0) },
          { label: "Average Score", value: data.tprmAvgScore != null ? `${data.tprmAvgScore}%` : "—" },
          { label: "Last Activity", value: formatTimestamp(data.tprmLastUsedAt) },
      ]);
    }
    if (include("gap")) {
      pushDetail("gap", "Gap Assessment", [
          { label: "Compliance Score", value: data.gapScore != null ? `${data.gapScore}%` : "—" },
          { label: "Compliance Label", value: data.gapComplianceLabel || "—" },
          { label: "Sections Completed", value: `${data.gapSectionsCompleted ?? 0} / ${data.gapSectionsTotal ?? 0}` },
          { label: "Questions Answered", value: `${data.gapAnswered ?? 0} / ${data.gapQuestionsTotal ?? 0}` },
          { label: "Identified Gaps", value: String(data.gapGaps ?? 0) },
          { label: "Status", value: data.gapSubmitted ? "Submitted" : data.gapHasDraft ? "Draft" : "Not started" },
          { label: "Last Activity", value: formatTimestamp(data.gapLastUsedAt) },
      ]);
    }
    if (include("ropa")) {
      pushDetail("ropa", "Records of Processing Activities (ROPA)", [
          { label: "Sections Filled", value: `${data.ropaSectionsFilled ?? 0} / ${data.ropaSectionsTotal ?? 0}` },
          { label: "Progress", value: `${data.ropaProgressPct ?? 0}%` },
          { label: "Submissions", value: String(data.ropaSubmitted ?? 0) },
          { label: "Has Draft", value: data.ropaHasDraft ? "Yes" : "No" },
          { label: "Status", value: data.ropaStatus || "—" },
          { label: "Last Activity", value: formatTimestamp(data.ropaLastUsedAt) },
      ]);
    }
    if (include("dspm") && data.dspmBreakdown?.length) {
      pushDetail(
        "dspm",
        "Data Security Posture (DSPM)",
        [
          { label: "Total Data Records", value: String(data.dspmTotal ?? 0) },
          { label: "Last Sync", value: formatTimestamp(data.dspmReceivedAt) },
        ],
        {
          table: {
            headers: ["Data Type", "Count"],
            rows: data.dspmBreakdown.map((d) => [d.label, String(d.count)]),
          },
        },
      );
    }
    if (include("breach")) {
      pushDetail("breach", "Breach Management", [
        { label: "Total Incidents", value: String(data.breachTotal ?? 0) },
        { label: "Manual Reports", value: String(data.breachManualTotal ?? 0) },
        { label: "Agent Reports", value: String(data.breachAgentTotal ?? 0) },
        { label: "Active / Open", value: String(data.breachActive ?? 0) },
        { label: "Pending Review", value: String(data.breachPending ?? 0) },
        { label: "Resolved", value: String(data.breachResolved ?? 0) },
        { label: "Critical / High Severity", value: String(data.breachCriticalHigh ?? 0) },
        { label: "Data Principals Affected", value: String(data.breachDpsAffected ?? 0) },
        { label: "Records Impacted", value: String(data.breachRecordsImpacted ?? 0) },
        { label: "Last Activity", value: formatTimestamp(data.breachLastUsedAt) },
      ]);
    }
    if (include("consent_variance")) {
      pushDetail("consent_variance", "Consent Variance", [
        { label: "Total Records", value: String(data.varianceTotal ?? 0) },
        { label: "Aligned", value: String(data.varianceAligned ?? 0) },
        { label: "With Variance", value: String(data.varianceWithVariance ?? 0) },
        { label: "Reconsent Required", value: String(data.varianceReconsentRequired ?? 0) },
        { label: "Reconsent Pending", value: String(data.varianceReconsentPending ?? 0) },
        { label: "Reconsent Completed", value: String(data.varianceReconsentCompleted ?? 0) },
        { label: "Alignment Rate", value: formatStatPct(data.varianceAligned, data.varianceTotal) },
        { label: "Last Updated", value: formatTimestamp(data.varianceLastUsedAt) },
      ]);
    }
    if (include("policy_store")) {
      pushDetail("policy_store", "Policy Store", [
        { label: "Total Policies", value: String(data.policyTotal ?? 0) },
        { label: "Uploaded", value: String(data.policyUploaded ?? 0) },
        { label: "AI Generated", value: String(data.policyGenerated ?? 0) },
        { label: "AI Provider Configured", value: data.policyAiConfigured ? "Yes" : "No" },
        { label: "Last Updated", value: formatTimestamp(data.policyLastUsedAt) },
      ]);
    }
    if (include("roadmap")) {
      pushDetail("roadmap", "Compliance Roadmap", [
        { label: "Overall Progress", value: `${data.roadmapOverallPct ?? 0}%` },
        {
          label: "Modules Fully Complete",
          value: `${data.roadmapModulesComplete ?? 0} / ${data.roadmapModulesTracked ?? 0}`,
        },
        { label: "Departments Tracked", value: String(data.roadmapDepartmentsTotal ?? 0) },
        { label: "Users Tracked", value: String(data.roadmapUsersTotal ?? 0) },
        licensedFor("gap") && {
          label: "Gap Assessment",
          value: `${data.roadmapAssessPct ?? 0}% (${data.roadmapAssessDeptComplete ?? 0}/${data.roadmapAssessDeptTotal ?? 0} depts)`,
        },
        licensedFor("ropa") && {
          label: "ROPA",
          value: `${data.roadmapRopaPct ?? 0}% (${data.roadmapRopaDeptComplete ?? 0}/${data.roadmapRopaDeptTotal ?? 0} depts)`,
        },
        licensedFor("pia") && {
          label: "PIA",
          value: `${data.roadmapPiaPct ?? 0}% (${data.roadmapPiaDeptComplete ?? 0}/${data.roadmapPiaDeptTotal ?? 0} depts)`,
        },
        licensedFor("tprm") && {
          label: "TPRM",
          value: `${data.roadmapTprmPct ?? 0}% (${data.roadmapTprmDeptComplete ?? 0}/${data.roadmapTprmDeptTotal ?? 0} depts)`,
        },
      ].filter(Boolean));
    }
    pushDepartmentAssessmentPdfSections(sections, data, showModule, { fullTable: true });
  }

  return sections;
}

export default function Reports() {
  const { currentUser } = useApp();
  const licensedModules = useMemo(() => tenantLicensedModules(currentUser), [currentUser]);
  const availableSources = useMemo(
    () => filterReportDataSources(DATA_SOURCES, licensedModules),
    [licensedModules],
  );
  const hasLicensedReportModules = availableSources.length > 0;
  const [activeTab, setActiveTab] = useState("executive");
  const [dataSource, setDataSource] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const [overview, setOverview] = useState(null);
  const [consentStats, setConsentStats] = useState(null);
  const [cookieStats, setCookieStats] = useState(null);
  const [dsarStats, setDsarStats] = useState(null);
  const [piaStats, setPiaStats] = useState(null);
  const [tprmStats, setTprmStats] = useState(null);
  const [gapStats, setGapStats] = useState(null);
  const [ropaStats, setRopaStats] = useState(null);
  const [dspmStats, setDspmStats] = useState(null);
  const [breachStats, setBreachStats] = useState(null);
  const [varianceStats, setVarianceStats] = useState(null);
  const [policyStats, setPolicyStats] = useState(null);
  const [roadmapStats, setRoadmapStats] = useState(null);
  const [departmentAssessments, setDepartmentAssessments] = useState(null);
  const [fetchErrors, setFetchErrors] = useState([]);

  useEffect(() => {
    try {
      localStorage.setItem("onedpdp_reports_visited", "1");
      if (!localStorage.getItem("onedpdp_reports_visited_at")) {
        localStorage.setItem("onedpdp_reports_visited_at", new Date().toISOString());
      }
    } catch {
      // ignore
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const errors = [];
    const licensed = tenantLicensedModules(currentUser);

    const tasks = [
      { key: "overview", path: "/dashboard/overview" },
      { key: "roadmap", path: "/dashboard/roadmap-progress" },
      { key: "consent", path: "/consent/stats" },
      { key: "cookie", path: "/consent/cookie-stats" },
      { key: "dsar", path: "/dsar/stats" },
      { key: "pia", path: "/pia/stats" },
      { key: "tprm", path: "/tprm/stats" },
      { key: "gap", path: "/gap-assessment/stats" },
      { key: "ropa", path: "/ropa/stats" },
      { key: "dspm", path: "/dspm/stats" },
      { key: "breach", path: "/breaches/stats" },
      { key: "variance", path: "/consent-variance/stats" },
      { key: "policy", path: "/policy-store/stats" },
      { key: "deptAssess", path: "/reports/department-assessments" },
    ].filter((task) => isReportFetchLicensed(licensed, task.key));

    const results = await Promise.all(
      tasks.map(async (task) => {
        const result = await fetchReportJson(task.path);
        return { key: task.key, ...result };
      }),
    );

    for (const result of results) {
      if (!result.ok) {
        if (result.status === 403) continue;
        errors.push(
          result.error ||
            `${result.key} stats unavailable${result.status ? ` (${result.status})` : ""}`,
        );
        continue;
      }

      const d = result.data;
      switch (result.key) {
        case "overview":
          setOverview(d);
          break;
        case "consent":
          setConsentStats(d);
          break;
        case "cookie":
          setCookieStats(d);
          break;
        case "dsar":
          setDsarStats(d);
          break;
        case "pia":
          setPiaStats({
            total: d.total ?? 0,
            sensitive: d.sensitive ?? 0,
            encrypted: d.encrypted ?? 0,
            unencrypted: d.unencrypted ?? 0,
            lastUsedAt: d.last_used_at ?? null,
          });
          break;
        case "tprm":
          setTprmStats({
            total: d.total ?? 0,
            submitted: d.submitted ?? 0,
            pending: d.pending ?? 0,
            highRisk: d.high_risk ?? 0,
            avgScore: d.avg_score ?? null,
            lastUsedAt: d.last_used_at ?? null,
          });
          break;
        case "gap":
          setGapStats({
            sectionsTotal: d.sections_total ?? 0,
            sectionsCompleted: d.sections_completed ?? 0,
            answered: d.answered ?? 0,
            questionsTotal: d.questions_total ?? 0,
            gaps: d.gaps ?? 0,
            score: d.score ?? null,
            complianceLabel: d.compliance_label ?? null,
            submitted: d.submitted ?? false,
            hasDraft: d.has_draft ?? false,
            lastUsedAt: d.last_used_at ?? null,
          });
          break;
        case "ropa":
          setRopaStats({
            sectionsTotal: d.sections_total ?? 0,
            sectionsFilled: d.sections_filled ?? 0,
            progressPct: d.progress_pct ?? 0,
            submitted: d.submitted ?? 0,
            hasDraft: d.has_draft ?? false,
            status: d.status ?? "not_started",
            lastUsedAt: d.last_used_at ?? null,
          });
          break;
        case "dspm":
          setDspmStats(d);
          break;
        case "breach":
          setBreachStats({
            total: d.total ?? 0,
            manualTotal: d.manual_total ?? 0,
            agentTotal: d.agent_total ?? 0,
            active: d.active ?? 0,
            pending: d.pending ?? 0,
            resolved: d.resolved ?? 0,
            investigating: d.investigating ?? 0,
            criticalHigh: d.critical_high ?? 0,
            dpsAffected: d.dps_affected_total ?? 0,
            recordsImpacted: d.records_impacted_total ?? 0,
            lastUsedAt: d.last_updated_at ?? null,
          });
          break;
        case "variance":
          setVarianceStats({
            total: d.total ?? 0,
            aligned: d.aligned ?? 0,
            reconsentRequired: d.reconsent_required ?? 0,
            reconsentPending: d.reconsent_pending ?? 0,
            reconsentCompleted: d.reconsent_completed ?? 0,
            withVariance: d.with_variance ?? 0,
            lastUsedAt: d.last_updated_at ?? null,
          });
          break;
        case "policy":
          setPolicyStats({
            total: d.total ?? 0,
            uploaded: d.uploaded ?? 0,
            generated: d.generated ?? 0,
            aiConfigured: d.ai_configured ?? false,
            lastUsedAt: d.last_updated_at ?? null,
          });
          break;
        case "roadmap":
          setRoadmapStats(d);
          break;
        case "deptAssess":
          setDepartmentAssessments(d);
          break;
        default:
          break;
      }
    }

    setFetchErrors(errors);
    setLastRefreshed(new Date());
    setLoading(false);
    setRefreshing(false);
  }, [currentUser]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!availableSources.length) return;
    if (!availableSources.some((source) => source.id === dataSource)) {
      setDataSource(availableSources[0].id);
    }
  }, [availableSources, dataSource]);

  const refresh = async () => {
    setRefreshing(true);
    await fetchAll();
  };

  const reportData = useMemo(() => {
    const consentAccepted = consentStats?.accepted ?? 0;
    const consentRevoked = consentStats?.revoked ?? 0;
    const consentDeclined = consentStats?.declined ?? 0;
    const consentTotal = consentStats
      ? effectiveStatTotal(consentStats.total, consentAccepted, consentRevoked, consentDeclined)
      : 0;

    const cookieAccepted = cookieStats?.accepted ?? 0;
    const cookieRejected = cookieStats?.rejected ?? 0;
    const cookieTotal = cookieStats
      ? effectiveStatTotal(cookieStats.total, cookieAccepted, cookieRejected)
      : 0;
    const cookiePending = Math.max(0, cookieTotal - cookieAccepted - cookieRejected);

    const dsarProcessed = dsarStats?.processed ?? 0;
    const dsarPending = dsarStats?.pending ?? 0;
    const dsarTotal = dsarStats
      ? effectiveStatTotal(dsarStats.total, dsarProcessed, dsarPending)
      : 0;
    const dsarRejected = Math.max(0, dsarTotal - dsarProcessed - dsarPending);

    const licensed = tenantLicensedModules(currentUser);
    const complianceScore = hasReportLicense(licensed, "gap")
      ? (gapStats?.score ?? overview?.compliance_score ?? null)
      : null;
    const complianceLabel = hasReportLicense(licensed, "gap")
      ? (gapStats?.complianceLabel ?? overview?.compliance_label ?? null)
      : null;

    const dspmBreakdown = (dspmStats?.payload || []).map((item) => ({
      label: item.data_type || "Unknown",
      count: item.data_count ?? 0,
    }));

    const riskItems = [];
    if (hasReportLicense(licensed, "gap") && (gapStats?.gaps ?? 0) > 0) {
      riskItems.push({
        area: "Gap Assessment",
        finding: `${gapStats.gaps} compliance gap(s) identified`,
        severity: (gapStats.score ?? 0) < 50 ? "High" : "Medium",
      });
    }
    if (hasReportLicense(licensed, "tprm") && (tprmStats?.highRisk ?? 0) > 0) {
      riskItems.push({
        area: "Third-Party Risk",
        finding: `${tprmStats.highRisk} vendor(s) below 50% risk score`,
        severity: "High",
      });
    }
    if (hasReportLicense(licensed, "pia") && (piaStats?.unencrypted ?? 0) > 0) {
      riskItems.push({
        area: "PIA",
        finding: `${piaStats.unencrypted} unencrypted sensitive field(s)`,
        severity: "Medium",
      });
    }
    if (hasReportLicense(licensed, "dsar") && dsarPending > 0) {
      riskItems.push({
        area: "DSAR",
        finding: `${dsarPending} pending data subject request(s)`,
        severity: dsarPending > 5 ? "High" : "Medium",
      });
    }
    if (
      hasReportLicense(licensed, "ropa") &&
      (ropaStats?.progressPct ?? 0) < 100 &&
      ropaStats?.status !== "submitted"
    ) {
      riskItems.push({
        area: "ROPA",
        finding: `Processing register ${ropaStats?.progressPct ?? 0}% complete`,
        severity: "Low",
      });
    }
    if (hasReportLicense(licensed, "breach") && (breachStats?.active ?? 0) > 0) {
      riskItems.push({
        area: "Breach Management",
        finding: `${breachStats.active} open breach incident(s)`,
        severity: (breachStats.criticalHigh ?? 0) > 0 ? "High" : "Medium",
      });
    }
    if (hasReportLicense(licensed, "consent_variance") && (varianceStats?.reconsentRequired ?? 0) > 0) {
      riskItems.push({
        area: "Consent Variance",
        finding: `${varianceStats.reconsentRequired} record(s) require reconsent`,
        severity: "Medium",
      });
    }
    if (hasReportLicense(licensed, "consent_variance") && (varianceStats?.reconsentPending ?? 0) > 0) {
      riskItems.push({
        area: "Consent Variance",
        finding: `${varianceStats.reconsentPending} reconsent request(s) pending`,
        severity: "Low",
      });
    }
    if (hasReportLicense(licensed, "policy_store") && (policyStats?.total ?? 0) === 0) {
      riskItems.push({
        area: "Policy Store",
        finding: "No DPDP policy documents uploaded or generated",
        severity: "Low",
      });
    }

    const roadmapAssess = parseRoadmapModule(roadmapStats?.assess);
    const roadmapRopa = parseRoadmapModule(roadmapStats?.ropa);
    const roadmapPia = parseRoadmapModule(roadmapStats?.pia);
    const roadmapTprm = parseRoadmapModule(roadmapStats?.tprm);
    const roadmapParts = [
      hasReportLicense(licensed, "gap") && { label: "Gap", ...roadmapAssess },
      hasReportLicense(licensed, "ropa") && { label: "ROPA", ...roadmapRopa },
      hasReportLicense(licensed, "pia") && { label: "PIA", ...roadmapPia },
      hasReportLicense(licensed, "tprm") && { label: "TPRM", ...roadmapTprm },
    ].filter(Boolean);
    const roadmapOverallPct =
      roadmapParts.length > 0
        ? Math.round(
            roadmapParts.reduce((sum, m) => sum + (m.progressPct ?? 0), 0) /
              roadmapParts.length,
          )
        : 0;
    const roadmapModulesComplete = roadmapParts.filter((m) => m.allComplete).length;
    const roadmapModulesTracked = roadmapParts.length;

    if (hasReportLicense(licensed, "roadmap") && roadmapStats && roadmapOverallPct < 100) {
      const incomplete = roadmapParts.filter((m) => !m.allComplete).map((m) => m.label);
      if (incomplete.length) {
        riskItems.push({
          area: "Compliance Roadmap",
          finding: `Incomplete modules: ${incomplete.join(", ")} (${roadmapOverallPct}% overall)`,
          severity: roadmapOverallPct < 50 ? "Medium" : "Low",
        });
      }
    }

    for (const mod of DEPT_ASSESSMENT_MODULES) {
      if (!hasReportLicense(licensed, mod.id)) continue;
      const payload = departmentAssessments?.[mod.key];
      const pendingCount = payload?.summary?.pending ?? 0;
      const inProgressCount = payload?.summary?.in_progress ?? 0;
      const area =
        mod.key === "pia"
          ? "PIA"
          : mod.key === "tprm"
            ? "Third-Party Risk"
            : mod.key === "ropa"
              ? "ROPA"
              : "Gap Assessment";
      if (pendingCount > 0) {
        const names = (payload.pending_departments || []).map((d) => d.department_name).join(", ");
        riskItems.push({
          area,
          finding: `${pendingCount} department(s) pending${names ? `: ${names}` : ""}`,
          severity: pendingCount > 3 ? "Medium" : "Low",
        });
      }
      if (inProgressCount > 0) {
        const names = (payload.in_progress_departments || []).map((d) => d.department_name).join(", ");
        riskItems.push({
          area,
          finding: `${inProgressCount} department(s) in progress${names ? `: ${names}` : ""}`,
          severity: "Low",
        });
      }
    }

    return {
      licensedModules: licensed,
      orgName: overview?.organization_name || currentUser?.organization_name || null,
      complianceScore,
      complianceLabel,
      consentTotal,
      consentAccepted,
      consentRevoked,
      consentDeclined,
      consentUpdatedAt: consentStats?.updated_at,
      cookieTotal,
      cookieAccepted,
      cookieRejected,
      cookiePending,
      cookieUpdatedAt: cookieStats?.updated_at,
      dsarTotal,
      dsarProcessed,
      dsarPending,
      dsarRejected,
      dsarUpdatedAt: dsarStats?.updated_at,
      piaTotal: piaStats?.total ?? 0,
      piaSensitive: piaStats?.sensitive ?? 0,
      piaEncrypted: piaStats?.encrypted ?? 0,
      piaUnencrypted: piaStats?.unencrypted ?? 0,
      piaLastUsedAt: piaStats?.lastUsedAt,
      tprmTotal: tprmStats?.total ?? 0,
      tprmSubmitted: tprmStats?.submitted ?? 0,
      tprmPending: tprmStats?.pending ?? 0,
      tprmHighRisk: tprmStats?.highRisk ?? 0,
      tprmAvgScore: tprmStats?.avgScore,
      tprmLastUsedAt: tprmStats?.lastUsedAt,
      gapSectionsTotal: gapStats?.sectionsTotal ?? 0,
      gapSectionsCompleted: gapStats?.sectionsCompleted ?? 0,
      gapAnswered: gapStats?.answered ?? 0,
      gapQuestionsTotal: gapStats?.questionsTotal ?? 0,
      gapGaps: gapStats?.gaps ?? 0,
      gapScore: gapStats?.score,
      gapComplianceLabel: gapStats?.complianceLabel,
      gapSubmitted: gapStats?.submitted ?? false,
      gapHasDraft: gapStats?.hasDraft ?? false,
      gapLastUsedAt: gapStats?.lastUsedAt,
      ropaSectionsTotal: ropaStats?.sectionsTotal ?? 0,
      ropaSectionsFilled: ropaStats?.sectionsFilled ?? 0,
      ropaProgressPct: ropaStats?.progressPct ?? 0,
      ropaSubmitted: ropaStats?.submitted ?? 0,
      ropaHasDraft: ropaStats?.hasDraft ?? false,
      ropaStatus: ropaStats?.status,
      ropaLastUsedAt: ropaStats?.lastUsedAt,
      dspmTotal: dspmStats?.total_data_count ?? 0,
      dspmReceivedAt: dspmStats?.received_at,
      dspmBreakdown,
      breachTotal: breachStats?.total ?? 0,
      breachManualTotal: breachStats?.manualTotal ?? 0,
      breachAgentTotal: breachStats?.agentTotal ?? 0,
      breachActive: breachStats?.active ?? 0,
      breachPending: breachStats?.pending ?? 0,
      breachResolved: breachStats?.resolved ?? 0,
      breachInvestigating: breachStats?.investigating ?? 0,
      breachCriticalHigh: breachStats?.criticalHigh ?? 0,
      breachDpsAffected: breachStats?.dpsAffected ?? 0,
      breachRecordsImpacted: breachStats?.recordsImpacted ?? 0,
      breachLastUsedAt: breachStats?.lastUsedAt,
      varianceTotal: varianceStats?.total ?? 0,
      varianceAligned: varianceStats?.aligned ?? 0,
      varianceWithVariance: varianceStats?.withVariance ?? 0,
      varianceReconsentRequired: varianceStats?.reconsentRequired ?? 0,
      varianceReconsentPending: varianceStats?.reconsentPending ?? 0,
      varianceReconsentCompleted: varianceStats?.reconsentCompleted ?? 0,
      varianceLastUsedAt: varianceStats?.lastUsedAt,
      policyTotal: policyStats?.total ?? 0,
      policyUploaded: policyStats?.uploaded ?? 0,
      policyGenerated: policyStats?.generated ?? 0,
      policyAiConfigured: policyStats?.aiConfigured ?? false,
      policyLastUsedAt: policyStats?.lastUsedAt,
      roadmapOverallPct,
      roadmapModulesComplete,
      roadmapModulesTracked,
      roadmapDepartmentsTotal: roadmapStats?.departments_total ?? 0,
      roadmapUsersTotal: roadmapStats?.users_total ?? 0,
      roadmapAssessPct: roadmapAssess.progressPct,
      roadmapAssessDeptComplete: roadmapAssess.deptComplete,
      roadmapAssessDeptTotal: roadmapAssess.deptTotal,
      roadmapRopaPct: roadmapRopa.progressPct,
      roadmapRopaDeptComplete: roadmapRopa.deptComplete,
      roadmapRopaDeptTotal: roadmapRopa.deptTotal,
      roadmapPiaPct: roadmapPia.progressPct,
      roadmapPiaDeptComplete: roadmapPia.deptComplete,
      roadmapPiaDeptTotal: roadmapPia.deptTotal,
      roadmapTprmPct: roadmapTprm.progressPct,
      roadmapTprmDeptComplete: roadmapTprm.deptComplete,
      roadmapTprmDeptTotal: roadmapTprm.deptTotal,
      departmentAssessments,
      riskItems,
    };
  }, [
    overview,
    consentStats,
    cookieStats,
    dsarStats,
    piaStats,
    tprmStats,
    gapStats,
    ropaStats,
    dspmStats,
    breachStats,
    varianceStats,
    policyStats,
    roadmapStats,
    departmentAssessments,
    currentUser,
  ]);

  const showModule = (id) => {
    if (dataSource !== "all" && dataSource !== id) return false;
    return hasReportLicense(licensedModules, id);
  };

  const executiveMetrics = buildExecutiveMetrics(reportData, showModule, dataSource);
  const scopedRisks = filterRiskItems(reportData.riskItems, showModule);
  const summaryRows = buildSummaryModuleRows(reportData, showModule);

  const handleExport = async () => {
    setExporting(true);
    try {
      const tabLabel = REPORT_TABS.find((t) => t.id === activeTab)?.label || "Report";
      const org = reportData.orgName || "Organization";
      const chartPrefs = loadChartPrefs();
      await exportSubmissionPdf({
        title: `${tabLabel} — Privacy Hub`,
        subtitle: DATA_SOURCES.find((s) => s.id === dataSource)?.label,
        module: "Privacy Reports",
        organization: org,
        filename: safeExportFilename(`${activeTab}_${dataSource}_report`),
        sections: buildReportSections({
          tab: activeTab,
          source: dataSource,
          data: reportData,
          orgName: org,
          chartPrefs,
          showModuleFn: showModule,
        }),
      });
    } catch (e) {
      showError(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const activeTabMeta = REPORT_TAB_META[activeTab] || REPORT_TAB_META.executive;
  const sourceLabel = DATA_SOURCES.find((s) => s.id === dataSource)?.label || "All Privacy Data";

  return (
    <div className="relative bg-slate-100 font-sans -m-4 lg:-m-6 min-h-full overflow-hidden">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-blue-200/30 blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-80 h-80 rounded-full bg-indigo-200/25 blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-96 h-96 rounded-full bg-cyan-200/20 blur-3xl" />
      </div>

      <header data-tour-id="tour-reports-header" className="relative bg-white/70 backdrop-blur-xl border-b border-white/50 px-4 sm:px-6 py-3.5 flex flex-wrap items-center justify-between gap-3 shadow-sm">
        <span className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent" />
        <div className="flex items-center gap-3">
          <div className="relative">
            <div
              className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 blur-md opacity-35 scale-110"
              aria-hidden
            />
            <div
              className="relative w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20 ring-1 ring-white/40"
              style={{ background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)" }}
            >
              <BarChart2 size={18} className="text-white drop-shadow-sm" />
            </div>
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-slate-900 leading-tight font-display tracking-tight inline-flex items-center gap-1.5">
              Privacy Reports
              <HelpTooltip
                content="Choose a data scope and a report type. The preview and the downloaded PDF use the same selection."
                size={14}
                position="bottom"
                maxWidth={320}
              />
            </h1>
            <p className="text-xs text-slate-500 hidden sm:block mt-0.5">
              Executive, summary, and detailed views across your privacy data
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="hidden md:flex items-center gap-1.5 text-xs text-slate-500 bg-white/50 backdrop-blur-sm border border-white/60 rounded-full px-2.5 py-1">
            <RefreshCw size={11} className={refreshing ? "animate-spin text-blue-600" : "text-slate-400"} />
            {refreshing
              ? "Refreshing…"
              : lastRefreshed
                ? `Updated ${formatTimestamp(lastRefreshed)}`
                : "Loading data…"}
          </p>
          <Button
            data-tour-id="tour-reports-refresh"
            variant="secondary"
            size="sm"
            title="Reload figures for the selected data scope."
            onClick={refresh}
            disabled={refreshing}
            className="!rounded-xl !border-white/60 !bg-white/50 backdrop-blur-sm"
            leftIcon={
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            }
          >
            Refresh
          </Button>
          <Button
            data-tour-id="tour-reports-export"
            size="sm"
            title="Download a PDF that matches the selected data scope and report type."
            onClick={handleExport}
            disabled={exporting || loading}
            loading={exporting}
            className="!rounded-xl !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !border-0 !shadow-md !shadow-blue-500/20"
            leftIcon={!exporting ? <FileDown size={14} /> : undefined}
          >
            Export PDF
          </Button>
        </div>
      </header>

      <div className="relative px-4 pb-2 pt-1 sm:px-6 sm:pb-3 sm:pt-2 space-y-4 w-full">
        {/* Report controls */}
        <div data-tour-id="tour-reports-panel" className={`${surfaceCard} px-3 pt-1 pb-2 sm:px-4 sm:pt-1.5 sm:pb-3 space-y-2 shadow-soft`}>
          <span className={premiumTopLine} />
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-2">
            <div className="flex-1 min-w-[200px] max-w-md">
              <Select
                label="Data scope"
                tooltip="Limits every figure, chart, and the PDF to one module, or to all licensed modules."
                value={hasLicensedReportModules ? dataSource : "all"}
                onChange={(e) => setDataSource(e.target.value)}
                disabled={!hasLicensedReportModules}
                className="!rounded-xl !border-slate-200/80 !bg-white/90"
              >
                {hasLicensedReportModules ? (
                  availableSources.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))
                ) : (
                  <option value="all">No licensed modules</option>
                )}
              </Select>
            </div>
            <div className="flex-1 flex items-end justify-end">
              <div className="flex flex-wrap gap-1 p-1.5 bg-white/40 backdrop-blur-md rounded-2xl border border-white/50 ring-1 ring-slate-900/[0.03]">
                {REPORT_TABS.map((tab) => {
                  const meta = REPORT_TAB_META[tab.id];
                  const TabIcon = meta?.icon || BarChart2;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      data-tour-id={
                        tab.id === "executive"
                          ? "tour-reports-tab-exec"
                          : tab.id === "summary"
                            ? "tour-reports-tab-summary"
                            : "tour-reports-tab-detail"
                      }
                      onClick={() => setActiveTab(tab.id)}
                      title={meta.description}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-xl transition-all duration-200 ${
                        isActive
                          ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                          : "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                      }`}
                    >
                      <TabIcon size={14} className={isActive ? "text-white/90" : "text-slate-400"} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100/80">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-800 bg-blue-50/70 backdrop-blur-sm border border-blue-200/50 rounded-full px-2.5 py-0.5 shadow-sm">
              <Filter size={12} className="text-blue-600" />
              {sourceLabel}
            </span>
            <span className="text-xs text-slate-300 hidden sm:inline">·</span>
            <p className="text-xs text-slate-500 leading-relaxed">{activeTabMeta.description}</p>
          </div>
          {!loading && fetchErrors.length > 0 && (
            <div className="flex items-start gap-2.5 text-xs text-amber-900 bg-amber-50/90 border border-amber-200/70 rounded-xl px-3 py-2.5">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Some report data could not be loaded</p>
                <p className="mt-0.5 text-amber-800/90">
                  {fetchErrors.slice(0, 3).join(" · ")}
                  {fetchErrors.length > 3 ? ` · +${fetchErrors.length - 3} more` : ""}
                  {" "}Try Refresh. If this persists, restart the backend so new stats endpoints are available.
                </p>
              </div>
            </div>
          )}
          {!loading && !hasLicensedReportModules && (
            <div className="flex items-start gap-2.5 text-xs text-amber-900 bg-amber-50/90 border border-amber-200/70 rounded-xl px-3 py-2.5">
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p>
                Report data is limited to modules licensed for your organization. No module
                licenses are assigned, so there is nothing to display.
              </p>
            </div>
          )}
          {!loading && !overview && fetchErrors.length === 0 && lastRefreshed && (
            <div className="flex items-start gap-2.5 text-xs text-slate-700 bg-slate-50/90 border border-slate-200/70 rounded-xl px-3 py-2.5">
              <AlertTriangle size={14} className="text-slate-500 mt-0.5 flex-shrink-0" />
              <p>
                Organization overview did not load. Confirm the backend is running and your session is active, then click Refresh.
              </p>
            </div>
          )}
        </div>

        {/* Visual analytics with chart customization */}
        {(activeTab === "executive" || activeTab === "summary" || activeTab === "detailed") &&
          hasLicensedReportModules && (
          <div data-tour-id="tour-reports-preview">
            <ReportChartSection
              reportData={{ ...reportData, riskItems: scopedRisks }}
              dataSource={dataSource}
              loading={loading}
              showModule={showModule}
              reportType={activeTab}
            />
          </div>
        )}

        {/* Executive Report */}
        {activeTab === "executive" && (
          <div className="space-y-5 animate-fade-in">
            <div className={`${surfaceCard} shadow-elevated`}>
              <span className={premiumTopLine} />
              <div className="relative px-5 sm:px-6 py-6 sm:py-7 bg-gradient-to-br from-blue-50/80 via-white/60 to-indigo-50/40 backdrop-blur-sm overflow-hidden">
                <div
                  className="pointer-events-none absolute -right-16 -top-16 w-56 h-56 rounded-full bg-blue-400/12 blur-3xl"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -left-10 bottom-0 w-40 h-40 rounded-full bg-indigo-400/10 blur-2xl"
                  aria-hidden
                />
                <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <PremiumIconBadge gradient="from-blue-500 to-indigo-600" size="lg">
                      <Building2 size={22} className="text-white drop-shadow-sm" />
                    </PremiumIconBadge>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        <SectionEyebrow>Organization</SectionEyebrow>
                        <HelpTooltip
                          content="The organisation this report is generated for."
                          size={12}
                          position="bottom"
                        />
                      </div>
                      <p
                        className="text-xl sm:text-2xl font-bold text-slate-900 truncate tracking-tight mt-1"
                        style={{ fontFamily: "Sora, sans-serif" }}
                      >
                        {loading ? "…" : reportData.orgName || "Your Organization"}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{sourceLabel}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-stretch gap-3 lg:gap-4">
                    {executiveMetrics.slice(0, 3).map((metric) => (
                      <div
                        key={metric.label}
                        className="flex items-center gap-3 bg-white/55 backdrop-blur-md rounded-2xl border border-white/60 shadow-sm ring-1 ring-slate-900/[0.04] px-4 py-3 min-w-[140px]"
                      >
                        {metric.label === "Compliance Score" && reportData.complianceScore != null && !loading && (
                          <ScoreRing
                            value={reportData.complianceScore}
                            size={52}
                            color={scoreColor(reportData.complianceScore)}
                          />
                        )}
                        <div>
                          <div className="flex items-center gap-1">
                            <SectionEyebrow>{metric.label}</SectionEyebrow>
                            {REPORT_METRIC_TIPS[metric.label] ? (
                              <HelpTooltip
                                content={REPORT_METRIC_TIPS[metric.label]}
                                size={12}
                                position="bottom"
                                maxWidth={300}
                              />
                            ) : null}
                          </div>
                          <p
                            className="text-xl sm:text-2xl font-bold tracking-tight mt-0.5"
                            style={{
                              fontFamily: "Sora, sans-serif",
                              color: metric.color || "#0f172a",
                            }}
                          >
                            {loading ? "…" : metric.value}
                          </p>
                          {metric.sub && (
                            <p className="text-[10px] text-slate-500 mt-0.5">{metric.sub}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {executiveMetrics.length > 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
              {executiveMetrics.slice(3).map((kpi) => (
                <div
                  key={kpi.label}
                  className="rounded-2xl ring-1 ring-slate-900/[0.04] shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <DashboardStatCard
                    label={kpi.label}
                    value={loading ? "…" : kpi.value}
                    sub={kpi.sub}
                    icon={executiveKpiIcon(kpi.label)}
                    color={kpi.tone || "primary"}
                    loading={loading}
                    tooltip={REPORT_METRIC_TIPS[kpi.label]}
                  />
                </div>
              ))}
            </div>
            )}

            <div className={`${surfaceCard} shadow-soft`}>
                <span className={premiumTopLine} />
                <div className="px-5 py-4 border-b border-slate-100/80 bg-gradient-to-r from-slate-50/70 to-white/50 backdrop-blur-sm flex items-center gap-3">
                  <PremiumIconBadge gradient="from-blue-500 to-indigo-600">
                    <ShieldCheck size={16} className="text-white drop-shadow-sm" />
                  </PremiumIconBadge>
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 font-display inline-flex items-center gap-1">
                      Key Risk Indicators
                      <HelpTooltip
                        content="Issues in the selected data scope that may need a review, with how serious each one is."
                        size={13}
                        position="bottom"
                        maxWidth={300}
                      />
                    </h2>
                    <p className="text-xs text-slate-500">Findings that may require immediate review</p>
                  </div>
                </div>
                <div className="p-5">
                  {loading ? (
                    <div className="animate-pulse space-y-2">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="h-12 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 rounded-xl" />
                      ))}
                    </div>
                  ) : scopedRisks.length === 0 ? (
                    <div className="flex items-center gap-3 text-emerald-900 bg-gradient-to-r from-emerald-50 to-teal-50/50 border border-emerald-200/60 rounded-2xl px-5 py-4 text-sm shadow-sm">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={20} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold font-display">No critical risk indicators</p>
                        <p className="text-xs text-emerald-700/90 mt-0.5">
                          No issues detected in the selected data scope.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-slate-200/80 ring-1 ring-slate-900/[0.03]">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] text-slate-500 uppercase tracking-[0.12em] bg-gradient-to-r from-slate-50/80 to-blue-50/40">
                            <th className="px-4 py-3.5 font-semibold">
                              <span className="inline-flex items-center gap-1">
                                Area
                                <HelpTooltip content="The privacy module this finding comes from." size={11} position="bottom" />
                              </span>
                            </th>
                            <th className="px-4 py-3.5 font-semibold">
                              <span className="inline-flex items-center gap-1">
                                Finding
                                <HelpTooltip content="What was found and why it may need a review." size={11} position="bottom" />
                              </span>
                            </th>
                            <th className="px-4 py-3.5 font-semibold">
                              <span className="inline-flex items-center gap-1">
                                Severity
                                <HelpTooltip content="How urgent the finding is: high, medium, or low." size={11} position="bottom" />
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {scopedRisks.map((r, i) => (
                              <tr
                                key={i}
                                className="border-t border-slate-100/80 hover:bg-blue-50/25 transition-colors duration-150"
                              >
                                <td className="px-4 py-3.5 font-medium text-slate-800">{r.area}</td>
                                <td className="px-4 py-3.5 text-slate-600">{r.finding}</td>
                                <td className="px-4 py-3.5">
                                  <RiskSeverityBadge severity={r.severity} />
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            <DepartmentAssessmentsSection
              showModule={showModule}
              reportData={reportData}
              loading={loading}
              formatTimestamp={formatDeptSubmittedAt}
              compact
            />
          </div>
        )}

        {/* Summary Report */}
        {activeTab === "summary" && (
          <div className="space-y-4 sm:space-y-5 animate-fade-in">
            <div className={surfaceCard}>
              <span className={premiumTopLine} />
              <div className="px-5 py-4 border-b border-slate-100/80 bg-gradient-to-r from-slate-50/70 via-white/50 to-blue-50/30">
                <SectionEyebrow>Summary</SectionEyebrow>
                <h2 className="text-sm font-semibold text-slate-900 font-display mt-0.5 inline-flex items-center gap-1">
                  Module headline figures
                  <HelpTooltip
                    content="One main figure and one supporting figure for each module in the selected data scope."
                    size={13}
                    position="bottom"
                    maxWidth={300}
                  />
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  One primary and one secondary figure for each module in {sourceLabel}. Timestamps, rates, and inventories are in the Detailed Report.
                </p>
              </div>
              <div className="p-5">
                {loading ? (
                  <div className="animate-pulse space-y-2">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="h-10 bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 rounded-xl" />
                    ))}
                  </div>
                ) : summaryRows.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    No modules in this data scope.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200/80 ring-1 ring-slate-900/[0.03]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] text-slate-500 uppercase tracking-[0.12em] bg-gradient-to-r from-slate-50/80 to-blue-50/40">
                          <th className="px-4 py-3.5 font-semibold">
                            <span className="inline-flex items-center gap-1">
                              Module
                              <HelpTooltip content="The licensed privacy module included in this data scope." size={11} position="bottom" />
                            </span>
                          </th>
                          <th className="px-4 py-3.5 font-semibold">
                            <span className="inline-flex items-center gap-1">
                              Primary metric
                              <HelpTooltip content="The main count for that module, such as how many were accepted or processed." size={11} position="bottom" />
                            </span>
                          </th>
                          <th className="px-4 py-3.5 font-semibold">
                            <span className="inline-flex items-center gap-1">
                              Secondary
                              <HelpTooltip content="A supporting rate, such as the share accepted or completed." size={11} position="bottom" />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryRows.map((row) => (
                          <tr
                            key={row[0]}
                            className="border-t border-slate-100/80 hover:bg-blue-50/25 transition-colors duration-150"
                          >
                            <td className="px-4 py-3.5 font-medium text-slate-800">{row[0]}</td>
                            <td className="px-4 py-3.5 text-slate-700">{row[1]}</td>
                            <td className="px-4 py-3.5 font-semibold text-slate-900">{row[2]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <DepartmentAssessmentsSection
              showModule={showModule}
              reportData={reportData}
              loading={loading}
              formatTimestamp={formatDeptSubmittedAt}
              compact
            />
          </div>
        )}

        {/* Detailed Report */}
        {activeTab === "detailed" && (
          <div className="space-y-4 sm:space-y-5 animate-fade-in">
            {showModule("consent") && (
              <DetailSection
                moduleId="consent"
                loading={loading}
                subtitle={`Last updated: ${formatTimestamp(reportData.consentUpdatedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total consent events" value={reportData.consentTotal} />
                  <MetricRow label="Acceptance rate" value={formatStatPct(reportData.consentAccepted, reportData.consentTotal)} />
                  <MetricRow label="Accepted" value={reportData.consentAccepted} />
                  <MetricRow label="Revoked" value={reportData.consentRevoked} />
                  <MetricRow label="Declined" value={reportData.consentDeclined} />
                </div>
              </DetailSection>
            )}

            {showModule("cookie") && (
              <DetailSection
                moduleId="cookie"
                loading={loading}
                subtitle={`Last updated: ${formatTimestamp(reportData.cookieUpdatedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total cookie events" value={reportData.cookieTotal} />
                  <MetricRow label="Acceptance rate" value={formatStatPct(reportData.cookieAccepted, reportData.cookieTotal)} />
                  <MetricRow label="Accepted" value={reportData.cookieAccepted} />
                  <MetricRow label="Rejected" value={reportData.cookieRejected} />
                  <MetricRow label="Pending resolution" value={reportData.cookiePending} />
                </div>
              </DetailSection>
            )}

            {showModule("dsar") && (
              <DetailSection
                moduleId="dsar"
                loading={loading}
                subtitle={`Last updated: ${formatTimestamp(reportData.dsarUpdatedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total requests" value={reportData.dsarTotal} />
                  <MetricRow label="Processed" value={reportData.dsarProcessed} />
                  <MetricRow label="Pending" value={reportData.dsarPending} highlight={reportData.dsarPending > 0} />
                  <MetricRow label="Rejected / other" value={reportData.dsarRejected} />
                  <MetricRow label="Completion rate" value={formatStatPct(reportData.dsarProcessed, reportData.dsarTotal)} />
                </div>
              </DetailSection>
            )}

            {showModule("pia") && (
              <DetailSection
                moduleId="pia"
                loading={loading}
                subtitle={`Last activity: ${formatTimestamp(reportData.piaLastUsedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total assessments" value={reportData.piaTotal} />
                  <MetricRow label="Sensitive processing flagged" value={reportData.piaSensitive} />
                  <MetricRow label="Encrypted fields" value={reportData.piaEncrypted} />
                  <MetricRow label="Unencrypted fields" value={reportData.piaUnencrypted} highlight={reportData.piaUnencrypted > 0} />
                  <MetricRow
                    label="Encryption coverage"
                    value={
                      reportData.piaSensitive > 0
                        ? formatStatPct(reportData.piaEncrypted, reportData.piaSensitive)
                        : "—"
                    }
                  />
                </div>
              </DetailSection>
            )}

            {showModule("tprm") && (
              <DetailSection
                moduleId="tprm"
                loading={loading}
                subtitle={`Last activity: ${formatTimestamp(reportData.tprmLastUsedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total vendor assessments" value={reportData.tprmTotal} />
                  <MetricRow label="Submitted" value={reportData.tprmSubmitted} />
                  <MetricRow label="Pending / draft" value={reportData.tprmPending} />
                  <MetricRow label="High risk (<50%)" value={reportData.tprmHighRisk} highlight={reportData.tprmHighRisk > 0} />
                  <MetricRow label="Average risk score" value={reportData.tprmAvgScore != null ? `${reportData.tprmAvgScore}%` : "—"} />
                  <MetricRow label="Submission rate" value={formatStatPct(reportData.tprmSubmitted, reportData.tprmTotal)} />
                </div>
              </DetailSection>
            )}

            {showModule("gap") && (
              <DetailSection
                moduleId="gap"
                loading={loading}
                subtitle={`Last activity: ${formatTimestamp(reportData.gapLastUsedAt, { loading })}`}
                extra={
                  reportData.gapScore != null && !loading ? (
                    <div className="flex items-center gap-2">
                      <ScoreRing
                        value={reportData.gapScore}
                        size={48}
                        color={scoreColor(reportData.gapScore)}
                      />
                      <div>
                        <p
                          className="text-lg font-bold leading-none"
                          style={{ color: scoreColor(reportData.gapScore), fontFamily: "Sora, sans-serif" }}
                        >
                          {reportData.gapScore}%
                        </p>
                        <p className="text-xs text-slate-500">{scoreLabel(reportData.gapScore)}</p>
                      </div>
                    </div>
                  ) : null
                }
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow
                    label="Sections completed"
                    value={`${reportData.gapSectionsCompleted} / ${reportData.gapSectionsTotal}`}
                  />
                  <MetricRow
                    label="Questions answered"
                    value={`${reportData.gapAnswered} / ${reportData.gapQuestionsTotal}`}
                  />
                  <MetricRow
                    label="Gaps identified"
                    value={reportData.gapGaps}
                    highlight={reportData.gapGaps > 0}
                  />
                  <MetricRow
                    label="Status"
                    value={
                      reportData.gapSubmitted
                        ? "Submitted"
                        : reportData.gapHasDraft
                          ? "Draft in progress"
                          : "Not started"
                    }
                  />
                </div>
              </DetailSection>
            )}

            {showModule("ropa") && (
              <DetailSection
                moduleId="ropa"
                loading={loading}
                subtitle={`Last activity: ${formatTimestamp(reportData.ropaLastUsedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow
                    label="Sections filled"
                    value={`${reportData.ropaSectionsFilled} / ${reportData.ropaSectionsTotal}`}
                  />
                  <MetricRow label="Completion" value={`${reportData.ropaProgressPct}%`} />
                  <MetricRow label="Submitted records" value={reportData.ropaSubmitted} />
                  <MetricRow label="Draft in progress" value={reportData.ropaHasDraft ? "Yes" : "No"} />
                  <MetricRow label="Workflow status" value={reportData.ropaStatus || "—"} />
                </div>
              </DetailSection>
            )}

            <DepartmentAssessmentsSection
              showModule={showModule}
              reportData={reportData}
              loading={loading}
              formatTimestamp={formatDeptSubmittedAt}
            />

            {showModule("dspm") && (
              <DetailSection
                moduleId="dspm"
                loading={loading}
                subtitle={`Last sync: ${formatTimestamp(reportData.dspmReceivedAt, { loading })}`}
              >
                <MetricRow label="Total data records" value={reportData.dspmTotal.toLocaleString()} />
                {loading ? (
                  <div className="mt-4 animate-pulse h-24 bg-slate-100 rounded-lg" />
                ) : reportData.dspmBreakdown.length === 0 ? (
                  <p className="mt-4 text-sm text-slate-500 py-6 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    No DSPM inventory data available yet.
                  </p>
                ) : (
                  <div className="mt-4 overflow-x-auto rounded-lg border border-slate-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] text-slate-500 uppercase tracking-wider bg-slate-50">
                          <th className="px-4 py-3 font-semibold">
                            <span className="inline-flex items-center gap-1">
                              Data Type
                              <HelpTooltip content="The kind of personal data the scanner found, such as email or phone." size={11} position="bottom" />
                            </span>
                          </th>
                          <th className="px-4 py-3 font-semibold text-right">
                            <span className="inline-flex items-center justify-end gap-1">
                              Record Count
                              <HelpTooltip content="How many records of this type were found." size={11} position="bottom" />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.dspmBreakdown.map((row, i) => (
                          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/80">
                            <td className="px-4 py-3 text-slate-700">{row.label}</td>
                            <td className="px-4 py-3 text-right font-bold tabular-nums text-slate-900" style={{ fontFamily: "Sora, sans-serif" }}>
                              {row.count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </DetailSection>
            )}

            {showModule("breach") && (
              <DetailSection
                moduleId="breach"
                loading={loading}
                subtitle={`Last activity: ${formatTimestamp(reportData.breachLastUsedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total incidents" value={reportData.breachTotal} />
                  <MetricRow label="Manual reports" value={reportData.breachManualTotal} />
                  <MetricRow label="Agent reports" value={reportData.breachAgentTotal} />
                  <MetricRow label="Active / open" value={reportData.breachActive} highlight={reportData.breachActive > 0} />
                  <MetricRow label="Pending review" value={reportData.breachPending} />
                  <MetricRow label="Resolved" value={reportData.breachResolved} />
                  <MetricRow label="Investigating" value={reportData.breachInvestigating} />
                  <MetricRow label="Critical / high severity" value={reportData.breachCriticalHigh} highlight={reportData.breachCriticalHigh > 0} />
                  <MetricRow label="Data principals affected" value={reportData.breachDpsAffected} />
                  <MetricRow label="Records impacted" value={reportData.breachRecordsImpacted} />
                  <MetricRow label="Resolution rate" value={formatStatPct(reportData.breachResolved, reportData.breachTotal)} />
                </div>
              </DetailSection>
            )}

            {showModule("consent_variance") && (
              <DetailSection
                moduleId="consent_variance"
                loading={loading}
                subtitle={`Last updated: ${formatTimestamp(reportData.varianceLastUsedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total records" value={reportData.varianceTotal} />
                  <MetricRow label="Aligned" value={reportData.varianceAligned} />
                  <MetricRow label="With variance" value={reportData.varianceWithVariance} highlight={reportData.varianceWithVariance > 0} />
                  <MetricRow label="Reconsent required" value={reportData.varianceReconsentRequired} highlight={reportData.varianceReconsentRequired > 0} />
                  <MetricRow label="Reconsent pending" value={reportData.varianceReconsentPending} />
                  <MetricRow label="Reconsent completed" value={reportData.varianceReconsentCompleted} />
                  <MetricRow label="Alignment rate" value={formatStatPct(reportData.varianceAligned, reportData.varianceTotal)} />
                </div>
              </DetailSection>
            )}

            {showModule("policy_store") && (
              <DetailSection
                moduleId="policy_store"
                loading={loading}
                subtitle={`Last updated: ${formatTimestamp(reportData.policyLastUsedAt, { loading })}`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Total policies" value={reportData.policyTotal} />
                  <MetricRow label="Uploaded documents" value={reportData.policyUploaded} />
                  <MetricRow label="AI-generated documents" value={reportData.policyGenerated} />
                  <MetricRow label="AI provider configured" value={reportData.policyAiConfigured ? "Yes" : "No"} />
                </div>
              </DetailSection>
            )}

            {showModule("roadmap") && (
              <DetailSection
                moduleId="roadmap"
                loading={loading}
                subtitle={`${reportData.roadmapModulesComplete} of ${reportData.roadmapModulesTracked} assessment modules fully complete`}
              >
                <div className="grid sm:grid-cols-2 gap-x-8">
                  <MetricRow label="Overall progress" value={`${reportData.roadmapOverallPct}%`} />
                  <MetricRow label="Departments tracked" value={reportData.roadmapDepartmentsTotal} />
                  <MetricRow label="Users tracked" value={reportData.roadmapUsersTotal} />
                  {hasReportLicense(licensedModules, "gap") && (
                    <MetricRow
                      label="Gap assessment"
                      value={`${reportData.roadmapAssessPct}% (${reportData.roadmapAssessDeptComplete}/${reportData.roadmapAssessDeptTotal} depts)`}
                    />
                  )}
                  {hasReportLicense(licensedModules, "ropa") && (
                    <MetricRow
                      label="ROPA"
                      value={`${reportData.roadmapRopaPct}% (${reportData.roadmapRopaDeptComplete}/${reportData.roadmapRopaDeptTotal} depts)`}
                    />
                  )}
                  {hasReportLicense(licensedModules, "pia") && (
                    <MetricRow
                      label="PIA"
                      value={`${reportData.roadmapPiaPct}% (${reportData.roadmapPiaDeptComplete}/${reportData.roadmapPiaDeptTotal} depts)`}
                    />
                  )}
                  {hasReportLicense(licensedModules, "tprm") && (
                    <MetricRow
                      label="TPRM"
                      value={`${reportData.roadmapTprmPct}% (${reportData.roadmapTprmDeptComplete}/${reportData.roadmapTprmDeptTotal} depts)`}
                    />
                  )}
                </div>
              </DetailSection>
            )}
          </div>
        )}

        {!loading && dataSource !== "all" && (
          <div className={`${surfaceCard} flex items-center justify-center gap-2.5 text-xs text-slate-600 px-4 py-3.5 shadow-sm`}>
            <span className={premiumTopLine} />
            <div className="w-8 h-8 rounded-xl bg-blue-50/80 flex items-center justify-center flex-shrink-0">
              <TrendingUp size={14} className="text-blue-600" />
            </div>
            <span>
              Showing <strong className="font-semibold text-slate-800">{sourceLabel}</strong> only.
              Change data scope above to view all modules.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
